import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { SEO } from '@/components/SEO';
import { SocialShare } from '@/components/SocialShare';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, User, ArrowLeft } from 'lucide-react';

const LostArtOfWalking = () => {
  const article = {
    title: "The Lost Art of Walking: Why Movement Heals the Mind",
    date: "September 20, 2025",
    readTime: "10 min read",
    author: "Piotr",
    category: "Mindfulness & Movement",
    image: "/lovable-uploads/walking-forest-road.jpg",
    imageCredit: "Pinterest/Forest Path Photography"
  };

  return (
    <>
      <SEO 
        title={article.title}
        description="Exploring why walking is one of the most powerful tools for mental and physical health — from ancient philosophy to modern neuroscience."
        canonicalUrl="https://mysticforest.club/articles/lost-art-of-walking"
        ogType="article"
        ogImage={`https://mysticforest.club${article.image}`}
        article={{
          publishedTime: "2025-10-18T00:00:00Z",
          author: article.author,
          section: article.category,
          tags: ["walking", "mindfulness", "mental health", "movement", "nature"]
        }}
      />
      <div className="min-h-screen bg-background">
        <Navigation />
        
        {/* Hero Section */}
        <section className="pt-32 pb-16 px-6">
          <div className="max-w-4xl mx-auto">
            <Button
              variant="ghost"
              onClick={() => window.history.back()}
              className="mb-8 -ml-4 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blog
            </Button>
            
            <div className="mb-8">
              <Badge className="mb-4">Mindfulness & Movement</Badge>
              <h1 className="text-4xl md:text-6xl font-serif font-light mb-6 text-foreground leading-tight">
                {article.title}
              </h1>
              
              <div className="flex items-center gap-6 text-sm text-muted-foreground mb-8">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <time dateTime="2025-09-20">{article.date}</time>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>{article.readTime}</span>
                </div>
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  <span>By {article.author}</span>
                </div>
              </div>
              
              <SocialShare 
                url={`https://mysticforest.club/articles/lost-art-of-walking`}
                title={article.title}
                description="Exploring why walking is one of the most powerful tools for mental and physical health — from ancient philosophy to modern neuroscience."
              />
            </div>
            
            <div className="relative overflow-hidden rounded-2xl mb-12">
              <img 
                src={article.image}
                alt="Forest road inviting a peaceful walk"
                className="w-full h-[400px] md:h-[500px] object-cover grayscale"
                loading="eager"
              />
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="px-6 pb-24">
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-lg max-w-none">
              <blockquote className="border-l-4 border-primary pl-6 my-8 italic text-xl text-muted-foreground">
                "All truly great thoughts are conceived while walking."
                <footer className="text-sm text-muted-foreground mt-2 not-italic">
                  — Friedrich Nietzsche, <em>Twilight of the Idols</em>, Leipzig: C. G. Naumann, 1889, p. 34
                </footer>
              </blockquote>

              <div className="space-y-8 text-lg leading-relaxed text-foreground">
                <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">The Forgotten Superpower</h2>

                <p className="mb-6 leading-relaxed">
                  In a world of constant stimulation, walking feels almost rebellious.
                  It requires no app, no gear, no destination — just time and curiosity.
                  Yet modern science keeps confirming what poets and philosophers knew for centuries: walking is one of the most powerful tools for mental and physical health.
                </p>

                <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

                <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">A Moving Brain</h2>

                <p className="mb-6 leading-relaxed">
                  A team at Stanford University (Oppezzo & Schwartz, 2014) found that creative output increased by 60 percent when participants walked compared to when they sat.
                  Functional MRI studies show that rhythmic movement stimulates the default-mode network — the brain's center for reflection, imagination, and problem-solving (Berman et al., 2012).
                </p>

                <blockquote className="border-l-4 border-primary pl-6 my-8 italic text-xl text-muted-foreground">
                  "Walking opens up the free flow of ideas."
                  <footer className="text-sm text-muted-foreground mt-2 not-italic">
                    — Rebecca Solnit, <em>Wanderlust: A History of Walking</em>, New York: Viking, 2000, p. 5
                  </footer>
                </blockquote>

                <p className="mb-6 leading-relaxed">
                  When we walk, our bodies fall into rhythm, and the mind follows.
                  Thoughts untangle. Tension releases. What felt overwhelming becomes navigable.
                </p>

                <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

                <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Medicine in Motion</h2>

                <p className="mb-6 leading-relaxed">
                  From a physiological perspective, walking is gentle precision.
                  Regular brisk walking — even 30 minutes a day — reduces the risk of heart disease by up to 35 percent and stroke by 27 percent (Harvard School of Public Health, 2022).
                  It lowers blood pressure, balances cortisol levels, and improves sleep quality.
                </p>

                <p className="mb-6 leading-relaxed">
                  But the mental benefits are even more striking.
                  In a 2019 meta-analysis published in Health Psychology Review, researchers found that regular walking significantly decreases symptoms of anxiety and depression, especially when done outdoors.
                  Nature enhances the effect: trees, light, and open space regulate our nervous system through sensory grounding (Ulrich, 1993).
                </p>

                <blockquote className="border-l-4 border-primary pl-6 my-8 italic text-xl text-muted-foreground">
                  "When you move through a landscape, it also moves through you."
                  <footer className="text-sm text-muted-foreground mt-2 not-italic">
                    — Robert Macfarlane, <em>The Old Ways: A Journey on Foot</em>, London: Hamish Hamilton, 2012, p. 75
                  </footer>
                </blockquote>

                <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

                <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Walking as Attention Training</h2>

                <p className="mb-6 leading-relaxed">
                  Psychologist Kelly McGonigal describes walking as "a form of moving meditation."
                  It's a way of synchronizing breath, body, and perception.
                  In her book <em>The Joy of Movement</em> (New York: Avery, 2019, p. 102) she writes:
                </p>

                <blockquote className="border-l-4 border-primary pl-6 my-8 italic text-xl text-muted-foreground">
                  "The rhythm of walking restores a sense of safety and connection — it reminds the body it belongs to the world."
                </blockquote>

                <p className="mb-6 leading-relaxed">
                  Walking invites mindfulness without effort: the crunch of gravel, the shift of wind, the texture of sunlight.
                  Unlike seated meditation, it channels restlessness into awareness.
                </p>

                <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

                <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">The Social Walk</h2>

                <p className="mb-6 leading-relaxed">
                  Historically, walking has also been communal.
                  Philosophers in ancient Athens taught while walking the colonnades of the Lyceum.
                  Today, therapists and coaches increasingly use walk-and-talk sessions because movement reduces social tension and deepens openness (Perkins et al., 2019).
                  Even short daily walks with a friend have been shown to strengthen emotional resilience and reduce loneliness.
                </p>

                <blockquote className="border-l-4 border-primary pl-6 my-8 italic text-xl text-muted-foreground">
                  "We walk ourselves into our best thoughts."
                  <footer className="text-sm text-muted-foreground mt-2 not-italic">
                    — Henry David Thoreau, <em>Walking</em>, Boston: Ticknor & Fields, 1862, p. 10
                  </footer>
                </blockquote>

                <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

                <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">The Rhythm of Return</h2>

                <p className="mb-6 leading-relaxed">
                  Every step is a small act of return — to breath, to presence, to the body's wisdom.
                  You don't need a forest or perfect conditions.
                  You need time, not goals.
                  Start with ten minutes. Feel the air, the pulse, the quiet conversation between your feet and the ground.
                </p>

                <p className="mb-6 leading-relaxed">
                  Walking may not change the world.
                  But it will change the way you move through it.
                </p>

                <p className="text-xl font-light italic text-accent mb-6">
                  To walk is to remember what being human feels like — one step, one breath, one presence at a time.
                </p>

                <div className="bg-card p-8 rounded-2xl mt-16 border border-border/20">
                  <h3 className="text-2xl font-serif font-light mb-4 text-foreground">References</h3>
                  <ol className="space-y-2 text-sm text-muted-foreground list-decimal list-inside">
                    <li>Berman, M. G., et al. (2012). The cognitive benefits of interacting with nature. <em>Psychological Science, 23</em>(9), 1207–1212.</li>
                    <li>Harvard T.H. Chan School of Public Health (2022). <em>Walking and Heart Health Report</em>.</li>
                    <li>McGonigal, K. (2019). <em>The Joy of Movement</em>. New York: Avery.</li>
                    <li>Nietzsche, F. (1889). <em>Twilight of the Idols</em>. Leipzig: C. G. Naumann.</li>
                    <li>Oppezzo, M., & Schwartz, D. L. (2014). Give Your Ideas Some Legs: The Positive Effect of Walking on Creative Thinking. <em>Journal of Experimental Psychology: Learning, Memory, and Cognition, 40</em>(4), 1142–1152.</li>
                    <li>Perkins, R., et al. (2019). Walk and Talk Therapy: Embodied Movement in Practice. <em>British Journal of Guidance & Counselling, 47</em>(6), 726–738.</li>
                    <li>Solnit, R. (2000). <em>Wanderlust: A History of Walking</em>. New York: Viking.</li>
                    <li>Thoreau, H. D. (1862). <em>Walking</em>. Boston: Ticknor & Fields.</li>
                    <li>Ulrich, R. S. (1993). Biophilia, Biophobia, and Natural Landscapes. In Kellert & Wilson (Eds.), <em>The Biophilia Hypothesis</em> (pp. 73–137). Washington DC: Island Press.</li>
                    <li>Macfarlane, R. (2012). <em>The Old Ways: A Journey on Foot</em>. London: Hamish Hamilton.</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        </article>

        <Footer />
      </div>
    </>
  );
};

export default LostArtOfWalking;
